import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSiteSettings } from '@/settings/useSiteSettings';
import type { SiteSettings } from '@/services/siteSettings';
import {
  getAdminConversations,
  getMessages,
  openAndReadMessages,
  sendMessage,
  markConversationRead,
  archiveConversation,
  unarchiveConversation,
  closeConversation,
  subscribeToAdminConversations,
  subscribeToMessages,
  subscribeToTyping,
  subscribeToAllTyping,
  emitTyping,
  emitStopTyping,
  uploadChatFile,
  checkUnreadConversations,
  type Conversation,
  type ChatMessage,
} from '@/services/supportChat';

const statusColors: Record<string, string> = {
  open: 'bg-green-50 text-green-700',
  closed: 'bg-surface-100 text-surface-500',
};

function getConvParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('conv');
}

const themes: Record<string, Record<string, string>> = {
  brand: {
    name: 'Brand',
    sidebarBg: 'bg-white',
    sidebarItem: 'text-surface-700 hover:bg-surface-100',
    sidebarActive: 'bg-brand-50 text-brand-700 border-r-2 border-brand-500',
    header: 'bg-gradient-to-r from-brand-600 to-brand-500',
    headerText: 'text-white',
    headerMuted: 'text-white/80',
    bubbleSent: 'bg-brand-600',
    bubbleReceived: 'bg-surface-100',
    bubbleText: 'text-white',
    bubbleReceivedText: 'text-surface-800',
    sendBtn: 'bg-brand-600 hover:bg-brand-700',
    accent: 'bg-brand-600',
    bodyBg: 'bg-white',
    textPrimary: 'text-surface-900',
    textSecondary: 'text-surface-500',
    textMuted: 'text-surface-400',
    border: 'border-surface-200',
    sidebarTitle: 'text-surface-900',
    sidebarCount: 'text-surface-400',
    hoverBg: 'hover:bg-surface-100',
  },
  sunset: {
    name: 'Sunset',
    sidebarBg: 'bg-white',
    sidebarItem: 'text-rose-700 hover:bg-rose-50',
    sidebarActive: 'bg-rose-50 text-rose-700 border-r-2 border-rose-500',
    header: 'bg-gradient-to-r from-rose-600 to-orange-500',
    headerText: 'text-white',
    headerMuted: 'text-white/80',
    bubbleSent: 'bg-rose-600',
    bubbleReceived: 'bg-rose-50',
    bubbleText: 'text-white',
    bubbleReceivedText: 'text-surface-800',
    sendBtn: 'bg-rose-600 hover:bg-rose-700',
    accent: 'bg-rose-600',
    bodyBg: 'bg-white',
    textPrimary: 'text-rose-900',
    textSecondary: 'text-rose-500',
    textMuted: 'text-rose-400',
    border: 'border-rose-200',
    sidebarTitle: 'text-rose-900',
    sidebarCount: 'text-rose-500',
    hoverBg: 'hover:bg-rose-50',
  },
  emerald: {
    name: 'Emerald',
    sidebarBg: 'bg-white',
    sidebarItem: 'text-emerald-700 hover:bg-emerald-50',
    sidebarActive:
      'bg-emerald-50 text-emerald-700 border-r-2 border-emerald-500',
    header: 'bg-gradient-to-r from-emerald-600 to-teal-500',
    headerText: 'text-white',
    headerMuted: 'text-white/80',
    bubbleSent: 'bg-emerald-600',
    bubbleReceived: 'bg-emerald-50',
    bubbleText: 'text-white',
    bubbleReceivedText: 'text-surface-800',
    sendBtn: 'bg-emerald-600 hover:bg-emerald-700',
    accent: 'bg-emerald-600',
    bodyBg: 'bg-white',
    textPrimary: 'text-emerald-900',
    textSecondary: 'text-emerald-500',
    textMuted: 'text-emerald-400',
    border: 'border-emerald-200',
    sidebarTitle: 'text-emerald-900',
    sidebarCount: 'text-emerald-500',
    hoverBg: 'hover:bg-emerald-50',
  },
  sapphire: {
    name: 'Sapphire',
    sidebarBg: 'bg-white',
    sidebarItem: 'text-blue-700 hover:bg-blue-50',
    sidebarActive: 'bg-blue-50 text-blue-700 border-r-2 border-blue-500',
    header: 'bg-gradient-to-r from-blue-700 to-indigo-500',
    headerText: 'text-white',
    headerMuted: 'text-white/80',
    bubbleSent: 'bg-blue-600',
    bubbleReceived: 'bg-blue-50',
    bubbleText: 'text-white',
    bubbleReceivedText: 'text-surface-800',
    sendBtn: 'bg-blue-600 hover:bg-blue-700',
    accent: 'bg-blue-600',
    bodyBg: 'bg-white',
    textPrimary: 'text-blue-900',
    textSecondary: 'text-blue-500',
    textMuted: 'text-blue-400',
    border: 'border-blue-200',
    sidebarTitle: 'text-blue-900',
    sidebarCount: 'text-blue-500',
    hoverBg: 'hover:bg-blue-50',
  },
};

export default function AdminSupport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings, updateSettings } = useSiteSettings();
  const s = settings.support;
  const th = themes[s?.theme] || themes.brand;
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState(s);
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [typingWho, setTypingWho] = useState('');
  const [typingConvs, setTypingConvs] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState<'sending' | 'sent' | ''>('');
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragOver(false);
  };
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    dragCounter.current = 0;
    const file = e.dataTransfer.files[0];
    if (!file || !activeId) return;
    setUploading(true);
    try {
      const url = await uploadChatFile(file);
      await sendMessage(activeId, `📎 ${file.name}: ${url}`, 'admin');
    } catch {}
    setUploading(false);
  };

  const clearConvUrl = () =>
    window.history.replaceState(null, '', '/admin/support');

  const loadRef = useRef(false);

  const openConv = useCallback(async (id: string) => {
    setActiveId(id);
    window.history.replaceState(null, '', `/admin/support?conv=${id}`);
    const data = await openAndReadMessages(id, 'admin');
    setMsgs(data);
    await markConversationRead(id, 'admin');
    setConvs((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unreadAdmin: 0 } : c)),
    );
  }, []);

  const load = useCallback(
    async (autoOpen = true) => {
      try {
        const all = await getAdminConversations(true);
        setConvs(all);
        if (autoOpen) {
          const convParam = getConvParam();
          if (convParam && all.find((c) => c.id === convParam)) {
            openConv(convParam);
          }
        }
      } catch (e) {
        console.error('Support load error:', e);
      }
      setLoading(false);
      // Check for delayed email notifications
      checkUnreadConversations('admin');
    },
    [openConv],
  );

  useEffect(() => {
    load();
  }, [location.search, load]);

  useEffect(() => {
    const sub = subscribeToAdminConversations(() => {
      load(false);
      if (activeId) getMessages(activeId, 'admin').then(setMsgs);
    });
    return () => sub?.unsubscribe();
  }, [activeId]);

  // Subscribe to all typing events for sidebar indicators
  useEffect(() => {
    const sub = subscribeToAllTyping('admin', (convId, who) => {
      setTypingConvs((prev) => {
        const next = { ...prev };
        if (who) next[convId] = who;
        else delete next[convId];
        return next;
      });
    });
    return () => {
      try {
        sub?.unsubscribe();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!activeId) return;
    const sub = subscribeToMessages(activeId, (payload: any) => {
      if (payload.new?.sender_role === 'customer') playNotifSound();
      getMessages(activeId).then(setMsgs);
    });
    const typingSub = subscribeToTyping(activeId, 'admin', (who) =>
      setTypingWho(who),
    );
    return () => {
      try {
        sub?.unsubscribe();
      } catch {}
      try {
        typingSub?.unsubscribe();
      } catch {}
    };
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  const playNotifSound = () => {
    try {
      const ctx = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      const play = (freq: number, time: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.12, ctx.currentTime + time);
        gain.gain.exponentialRampToValueAtTime(
          0.001,
          ctx.currentTime + time + dur,
        );
        osc.start(ctx.currentTime + time);
        osc.stop(ctx.currentTime + time + dur);
      };
      play(660, 0, 0.12);
      play(880, 0.1, 0.15);
    } catch {}
  };

  const handleInputChange = (val: string) => {
    setInput(val);
    if (activeId && val.trim()) {
      emitTyping(activeId, 'admin', 'Support');
    } else if (activeId) {
      emitStopTyping(activeId, 'admin', 'Support');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeId) return;
    setUploading(true);
    try {
      const url = await uploadChatFile(file);
      await sendMessage(activeId, `📎 ${file.name}: ${url}`, 'admin');
    } catch {}
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSend = async () => {
    if (!input.trim() || !activeId || sending) return;
    if (activeId) emitStopTyping(activeId, 'admin', 'Support');
    setSending(true);
    try {
      await sendMessage(activeId, input.trim(), 'admin');
      setInput('');
    } catch {}
    setSending(false);
  };

  const toggleArchive = async (id: string, currentlyArchived: boolean) => {
    if (currentlyArchived) await unarchiveConversation(id);
    else await archiveConversation(id);
    setConvs((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, archived: !currentlyArchived } : c,
      ),
    );
    setActiveId(null);
    load(false);
  };

  const activeConv = convs.find((c) => c.id === activeId);

  const sorted = [...convs].sort((a, b) => {
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const filtered = sorted.filter(
    (c) =>
      !search ||
      c.customerName.toLowerCase().includes(search.toLowerCase()) ||
      c.subject.toLowerCase().includes(search.toLowerCase()) ||
      c.customerEmail.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div
      className={`h-[calc(100vh-100px)] flex gap-0 rounded-xl border overflow-hidden ${th.bodyBg} ${th.border}`}>
      {/* Sidebar - hidden on mobile when a conversation is active */}
      <div
        className={`w-full lg:w-80 border-r flex flex-col shrink-0 ${activeId ? 'hidden lg:flex' : 'flex'} ${th.sidebarBg} ${th.border}`}>
        <div className="px-4 py-3 border-b border-surface-100 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className={`text-sm font-bold ${th.sidebarTitle}`}>Inbox</h2>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${th.sidebarCount}`}>
                {convs.filter((c) => !c.archived).length} active
              </span>
              <button
                onClick={() => setShowSettings(true)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-600 transition-colors"
                title="Support Settings">
                <i className="ri-settings-3-line text-sm" />
              </button>
            </div>
          </div>
          <div className="relative">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-400 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full pl-7 pr-3 py-1.5 rounded-lg border text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${th.border} ${th.textSecondary}`}
            />
          </div>
          <div className="flex gap-1 px-3 py-1.5 border-b border-surface-100">
            <button
              onClick={() => {
                const csv = [
                  [
                    'Customer',
                    'Email',
                    'Subject',
                    'Status',
                    'Messages',
                    'Created',
                  ].join(','),
                ];
                convs.forEach((c) => {
                  csv.push(
                    [
                      `"${c.customerName}"`,
                      `"${c.customerEmail}"`,
                      `"${c.subject}"`,
                      c.status,
                      String(c.unreadAdmin + c.unreadCustomer),
                      c.createdAt,
                    ].join(','),
                  );
                });
                const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'support-conversations.csv';
                a.click();
              }}
              className={`flex-1 px-2 py-1 rounded text-[10px] ${th.textSecondary} ${th.hoverBg} transition-colors text-center`}
              title="Export CSV">
              <i className="ri-file-excel-2-line mr-1" />
              CSV
            </button>
            <button
              onClick={() => {
                if (!activeConv) return;
                const msgsText = msgs
                  .map(
                    (m) =>
                      `[${new Date(m.createdAt).toLocaleString()}] ${m.senderRole === 'admin' ? 'Support' : activeConv.customerName}: ${m.message}`,
                  )
                  .join('\n');
                const blob = new Blob(
                  [
                    `Conversation: ${activeConv.subject || 'No subject'}\nCustomer: ${activeConv.customerName}\nEmail: ${activeConv.customerEmail}\n\n${msgsText}`,
                  ],
                  { type: 'text/plain' },
                );
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `transcript-${activeConv.customerName.replace(/\s/g, '-')}.txt`;
                a.click();
              }}
              className="flex-1 px-2 py-1 rounded text-[10px] text-surface-500 hover:bg-surface-100 transition-colors text-center"
              title="Download Transcript">
              <i className="ri-file-text-line mr-1" />
              Transcript
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-surface-300 text-xl" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-surface-400">
              No conversations
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => openConv(c.id)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-surface-200/50 transition-colors ${activeId === c.id ? th.sidebarActive : th.sidebarItem} ${c.archived ? 'opacity-60' : ''} ${c.isNewCustomer && !c.archived ? 'bg-blue-50/30' : ''}`}>
                <div className="relative shrink-0">
                  {c.customerAvatar ? (
                    <img
                      src={c.customerAvatar}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${c.isNewCustomer ? 'bg-blue-100 text-blue-700' : 'bg-brand-100 text-brand-600'}`}>
                      {c.customerName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${c.isOnline ? 'bg-green-500' : 'bg-surface-300'}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p
                      className={`text-sm font-semibold truncate ${th.textPrimary}`}>
                      {c.customerName}
                    </p>
                    <span className={`text-[10px] shrink-0 ${th.textMuted}`}>
                      {new Date(c.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${th.textSecondary}`}>
                    {c.subject || 'No subject'}
                  </p>
                  {typingConvs[c.id] && (
                    <p className="text-[10px] text-brand-600 font-medium mt-0.5 flex items-center gap-1">
                      <span className="flex gap-0.5">
                        <span
                          className="w-1 h-1 rounded-full bg-brand-500 animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <span
                          className="w-1 h-1 rounded-full bg-brand-500 animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <span
                          className="w-1 h-1 rounded-full bg-brand-500 animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </span>
                      Typing…
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[c.status] || 'bg-surface-100 text-surface-500'}`}>
                      {c.status}
                    </span>
                    {c.archived && (
                      <span className="px-1.5 py-0.5 rounded bg-surface-200 text-surface-500 text-[10px] font-medium">
                        Archived
                      </span>
                    )}
                    {c.isNewCustomer && !c.archived && (
                      <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium">
                        New
                      </span>
                    )}
                  </div>
                </div>
                {c.unreadAdmin > 0 && (
                  <span className="w-5 h-5 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                    {c.unreadAdmin}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      {activeConv ? (
        <div
          className={`flex-1 flex flex-col min-w-0 ${activeId ? 'flex' : 'hidden lg:flex'}`}>
          <div
            className={`flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0 ${th.header} ${th.headerText}`}>
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => {
                  setActiveId(null);
                  clearConvUrl();
                }}
                className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 -ml-1">
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <div className="relative shrink-0">
                {activeConv.customerAvatar ? (
                  <img
                    src={activeConv.customerAvatar}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${activeConv.isNewCustomer ? 'bg-blue-100 text-blue-700' : 'bg-brand-100 text-brand-600'}`}>
                    {activeConv.customerName.charAt(0).toUpperCase()}
                  </div>
                )}
                <span
                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${activeConv.isOnline ? 'bg-green-500' : 'bg-surface-300'}`}
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-semibold ${th.headerText}`}>
                    {activeConv.customerName}
                  </p>
                  {activeConv.isNewCustomer && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium">
                      New
                    </span>
                  )}
                  {activeConv.archived && (
                    <span className="px-1.5 py-0.5 rounded bg-surface-200 text-surface-500 text-[10px] font-medium">
                      Archived
                    </span>
                  )}
                </div>
                <p className={`text-xs truncate ${th.headerMuted}`}>
                  {activeConv.subject || 'No subject'} ·{' '}
                  {activeConv.customerEmail}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() =>
                  toggleArchive(activeConv.id, activeConv.archived)
                }
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${activeConv.archived ? 'border-brand-200 text-brand-600 hover:bg-brand-50' : 'border-surface-300 text-white hover:bg-surface-100'}`}>
                <i
                  className={`${activeConv.archived ? 'ri-inbox-2-line' : 'ri-archive-line'} mr-1`}
                />
                {activeConv.archived ? 'Restore' : 'Archive'}
              </button>
              {activeConv.status === 'open' && (
                <button
                  onClick={async () => {
                    await closeConversation(activeConv.id);
                    // Send transcript email
                    try {
                      const supabase = (await import('@/lib/supabase'))
                        .supabase;
                      const token = (await supabase!.auth.getSession()).data
                        .session?.access_token;
                      if (token) {
                        const msgText = msgs
                          .map(
                            (m) =>
                              `[${new Date(m.createdAt).toLocaleString()}] ${m.senderRole === 'admin' ? 'Support' : activeConv.customerName}: ${m.message}`,
                          )
                          .join('\n');
                        const transcript = `Conversation Closed\nCustomer: ${activeConv.customerName}\nEmail: ${activeConv.customerEmail}\nSubject: ${activeConv.subject || 'No subject'}\n\n${msgText}`;
                        await fetch(
                          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-new-conversation`,
                          {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                              conversationId: activeConv.id,
                              senderRole: 'admin',
                              senderDisplayName: activeConv.customerName,
                              recipientEmail: activeConv.customerEmail,
                              subject: 'Conversation Closed',
                              unseenMessages: [transcript],
                            }),
                          },
                        );
                      }
                    } catch {}
                    setConvs((prev) =>
                      prev.map((c) =>
                        c.id === activeConv.id ? { ...c, status: 'closed' } : c,
                      ),
                    );
                    setActiveId(null);
                    clearConvUrl();
                    load(false);
                  }}
                  className="px-3 py-1.5 rounded-lg border border-white-200 text-red-600 text-xs bg-slate-50 font-medium hover:bg-red-50 transition-colors">
                  Close & Send Transcript
                </button>
              )}
              {activeConv.status === 'closed' && (
                <span className="text-xs text-white italic">Closed</span>
              )}
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto p-5 space-y-3 relative"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            {dragOver && (
              <div className="absolute inset-0 z-10 bg-brand-500/10 border-2 border-dashed border-brand-500 rounded-lg flex items-center justify-center">
                <div className="bg-white rounded-xl px-6 py-4 shadow-lg text-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-brand-500 mb-2" />
                  <p className="text-sm font-semibold text-surface-900">
                    Drop file to attach
                  </p>
                </div>
              </div>
            )}
            {msgs.map((m) => {
              const isAdmin = m.senderRole === 'admin';
              const fileMatch = m.message.match(/^📎\s(.+?):\s(.+)$/);
              const isFile = !!fileMatch;
              const ext = isFile
                ? fileMatch![1].split('.').pop()?.toLowerCase()
                : '';
              const isImage =
                isFile &&
                ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'].includes(
                  ext || '',
                );
              return (
                <div
                  key={m.id}
                  className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isAdmin ? `${th.bubbleSent} ${th.bubbleText} rounded-br-md` : `${th.bubbleReceived} ${th.bubbleReceivedText} rounded-bl-md`}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-medium opacity-70">
                        {isAdmin ? 'You' : activeConv.customerName}
                      </span>
                    </div>
                    {isImage ? (
                      <img
                        src={fileMatch![2]}
                        alt={fileMatch![1]}
                        className="max-w-full rounded-lg cursor-pointer"
                        onClick={() => setLightbox(fileMatch![2])}
                        loading="lazy"
                      />
                    ) : isFile ? (
                      <a
                        href={fileMatch![2]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${isAdmin ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-surface-700 hover:bg-surface-50 border border-surface-200'} transition-colors`}>
                        <i className="ri-file-line" />
                        <span className="text-sm font-medium">
                          {fileMatch![1]}
                        </span>
                        <i className="ri-download-line text-xs ml-1" />
                      </a>
                    ) : (
                      m.message
                    )}
                    <div
                      className={`flex items-center gap-1 mt-1 ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <p
                        className={`text-[10px] ${isAdmin ? 'text-white/60' : 'text-surface-400'}`}>
                        {new Date(m.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      {isAdmin && s.readReceipts && (
                        <span
                          className={`text-[11px] ${m.seen ? 'text-blue-400' : 'text-white/40'}`}>
                          {m.seen ? (
                            <i className="ri-check-double-fill" />
                          ) : (
                            <i className="ri-check-line" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {typingWho && (
              <div className="flex items-center gap-2 px-5 py-1.5 text-xs text-surface-400 italic bg-white border-t border-surface-50">
                <span className="flex gap-0.5">
                  <span
                    className="w-1 h-1 rounded-full bg-surface-400 animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-1 h-1 rounded-full bg-surface-400 animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-1 h-1 rounded-full bg-surface-400 animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </span>
                {typingWho} is typing…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {activeConv.status === 'open' && (
            <div className="px-4 py-3 border-t border-surface-100 flex gap-2 shrink-0">
              <input
                type="text"
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type your reply..."
                className="flex-1 px-3.5 py-2.5 rounded-xl border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-10 h-10 rounded-xl border border-surface-300 text-surface-500 hover:text-brand-600 hover:border-brand-400 flex items-center justify-center shrink-0 transition-colors"
                title="Attach file">
                {uploading ? (
                  <i className="ri-loader-4-line animate-spin text-sm" />
                ) : (
                  <i className="ri-file-line text-lg" />
                )}
              </button>
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className={`px-4 py-2.5 rounded-xl disabled:bg-surface-300 text-white text-sm font-semibold flex items-center gap-2 transition-colors ${th.sendBtn}`}>
                {sending ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  'Send'
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`flex-1 flex items-center justify-center text-surface-400 ${activeId ? 'hidden lg:flex' : 'flex'}`}>
          <div className="text-center">
            <i className="ri-chat-1-line text-5xl mb-4 opacity-50" />
            <p className="text-sm font-medium">Select a conversation</p>
            <p className="text-xs mt-1">
              Choose a conversation from the left panel to view messages
            </p>
          </div>
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox('')}>
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            onClick={() => setLightbox('')}>
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={lightbox}
            alt=""
            className="max-w-full max-h-full rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Settings Drawer */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSettings(false)}
          />
          <div className="relative ml-auto w-full max-w-sm bg-white h-full shadow-2xl overflow-y-auto">
            <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-sm font-bold text-surface-900">
                Support Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-5 space-y-6">
              {/* Auto Reply */}
              <div>
                <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                  Auto Reply
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-surface-700">
                      Enable auto-reply
                    </span>
                    <input
                      type="checkbox"
                      checked={draft.autoReplyEnabled}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          autoReplyEnabled: e.target.checked,
                        })
                      }
                      className="w-4 h-4 accent-brand-600 rounded"
                    />
                  </label>
                  <textarea
                    value={draft.autoReplyMessage}
                    onChange={(e) =>
                      setDraft({ ...draft, autoReplyMessage: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    placeholder="Auto-reply message..."
                  />
                </div>
              </div>

              <div className="border-t border-surface-100" />

              {/* Email Notifications */}
              <div>
                <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                  Email Notifications
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-surface-700">
                      Send email on new conversation
                    </span>
                    <input
                      type="checkbox"
                      checked={draft.emailNotifications}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          emailNotifications: e.target.checked,
                        })
                      }
                      className="w-4 h-4 accent-brand-600 rounded"
                    />
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-surface-700">Delay</span>
                    <input
                      type="number"
                      value={draft.emailDelayMinutes}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          emailDelayMinutes: Math.max(
                            1,
                            Number(e.target.value),
                          ),
                        })
                      }
                      className="w-16 px-2 py-1.5 rounded-lg border border-surface-300 text-sm text-center"
                      min={1}
                    />
                    <span className="text-sm text-surface-500">minutes</span>
                  </div>
                  <p className="text-xs text-surface-400">
                    Email is sent if no admin responds within the delay.
                    Requires RESEND_API_KEY configured.
                  </p>
                </div>
              </div>

              <div className="border-t border-surface-100" />

              {/* Resend API Key */}
              <div>
                <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                  Email Provider
                </h4>
                <div className="space-y-2">
                  <label className="block text-xs text-surface-500">
                    Resend API Key
                  </label>
                  <input
                    type="password"
                    value={draft.resendApiKey}
                    onChange={(e) =>
                      setDraft({ ...draft, resendApiKey: e.target.value })
                    }
                    placeholder="re_..."
                    className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        if (!draft.resendApiKey) {
                          alert('Enter a Resend API key first.');
                          return;
                        }
                        setTesting('sending');
                        try {
                          const supabase = (await import('@/lib/supabase'))
                            .supabase;
                          const token = (await supabase!.auth.getSession()).data
                            .session?.access_token;
                          const VITE_SUPABASE_URL = import.meta.env
                            .VITE_SUPABASE_URL;
                          const res = await fetch(
                            `${VITE_SUPABASE_URL}/functions/v1/notify-new-conversation`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({
                                conversationId: 'test',
                                customerName: 'Test User',
                                customerEmail: 'admin@motionsdio.com',
                                subject: 'Test Email',
                                message:
                                  'This is a test email from the support settings.',
                              }),
                            },
                          );
                          if (res.ok) {
                            setTesting('sent');
                            setTimeout(() => setTesting(''), 3000);
                          } else {
                            const d = await res.json();
                            alert('Error: ' + (d.error || res.status));
                            setTesting('');
                          }
                        } catch (e) {
                          alert('Network error: ' + String(e));
                          setTesting('');
                        }
                      }}
                      disabled={testing === 'sending'}
                      className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-xs font-medium transition-colors">
                      {testing === 'sending'
                        ? 'Sending…'
                        : testing === 'sent'
                          ? '✓ Sent!'
                          : 'Test Email'}
                    </button>
                    <span className="text-xs text-surface-400">
                      A test email will be sent to admin@motionsdio.com
                    </span>
                  </div>
                  <p className="text-xs text-surface-400">
                    Get a free API key at{' '}
                    <a
                      href="https://resend.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline">
                      resend.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="border-t border-surface-100" />

              {/* Business Hours */}
              <div>
                <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                  Business Hours
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-surface-700">
                      Enable business hours
                    </span>
                    <input
                      type="checkbox"
                      checked={draft.businessHoursEnabled}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          businessHoursEnabled: e.target.checked,
                        })
                      }
                      className="w-4 h-4 accent-brand-600 rounded"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-surface-500 mb-1">
                        Start time
                      </label>
                      <input
                        type="time"
                        value={draft.businessHoursStart}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            businessHoursStart: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-surface-500 mb-1">
                        End time
                      </label>
                      <input
                        type="time"
                        value={draft.businessHoursEnd}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            businessHoursEnd: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm"
                      />
                    </div>
                  </div>
                  <label className="block text-xs text-surface-500">
                    Away message
                  </label>
                  <textarea
                    value={draft.awayMessage}
                    onChange={(e) =>
                      setDraft({ ...draft, awayMessage: e.target.value })
                    }
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm"
                  />
                </div>
              </div>

              <div className="border-t border-surface-100" />

              {/* Preferences */}
              <div>
                <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                  Preferences
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-surface-700">
                      Read receipts
                    </span>
                    <input
                      type="checkbox"
                      checked={draft.readReceipts}
                      onChange={(e) =>
                        setDraft({ ...draft, readReceipts: e.target.checked })
                      }
                      className="w-4 h-4 accent-brand-600 rounded"
                    />
                  </label>
                  <div>
                    <label className="block text-xs text-surface-500 mb-1">
                      Max message length
                    </label>
                    <input
                      type="number"
                      value={draft.maxMessageLength}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          maxMessageLength: Math.max(
                            100,
                            Number(e.target.value),
                          ),
                        })
                      }
                      className="w-24 px-3 py-2 rounded-lg border border-surface-300 text-sm"
                      min={100}
                      max={50000}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-surface-100" />

              {/* Theme */}
              <div>
                <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                  Chat Theme
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(themes).map(([key, tm]) => (
                    <button
                      key={key}
                      onClick={() => setDraft({ ...draft, theme: key })}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${draft.theme === key ? 'border-brand-500 bg-brand-50/50' : 'border-surface-200 hover:border-surface-300'}`}>
                      <div className={`w-full h-8 rounded-lg ${tm.header}`} />
                      <span className="text-xs font-medium text-surface-700">
                        {tm.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Save */}
              <button
                onClick={async () => {
                  await updateSettings({ ...settings, support: draft });
                  setShowSettings(false);
                }}
                className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
